import { Controller } from "@hotwired/stimulus"

// Controller to confirm DNA (Does Not Apply) checkbox changes when data would be lost
// Prevents accidental data loss by confirming before clearing answers
// Mount this on the question frame, with the DNA checkbox as a target
export default class extends Controller {
  static targets = ["dnaCheckbox", "dataContainer"]

  // Intercept DNA checkbox changes to confirm if answers would be cleared
  confirmChange(event) {
    const checkbox = event.target

    // Only confirm when CHECKING DNA (not unchecking)
    if (!checkbox.checked) {
      return
    }

    const questionId = checkbox.dataset.questionId
    const shouldConfirm = this.hasAnswersToClear(questionId)

    if (shouldConfirm) {
      const message = this.getConfirmationMessage()

      if (!window.confirm(message)) {
        // User cancelled - prevent the change and uncheck the box
        event.preventDefault()
        checkbox.checked = false
        return
      }
    }
  }

  // Check if the question has any answers that would be cleared
  hasAnswersToClear(questionId) {
    // Check if the main question has an answer
    const mainQuestionHasAnswer = this.questionHasAnswer(questionId)

    // Check if any data containers (for specialized questions) have data
    const dataContainersHaveData = this.hasDataContainerRows()

    // Check if any files are uploaded
    const uploadedFilesCount = parseInt(this.element.dataset.uploadedFilesCount || '0', 10)
    const hasUploadedFiles = uploadedFilesCount > 0

    return mainQuestionHasAnswer || dataContainersHaveData || hasUploadedFiles
  }

  // Check if a specific question has an answer
  questionHasAnswer(questionId) {
    // Search within the frame (this.element)
    const inputs = this.element.querySelectorAll(
      `input[data-question-id="${questionId}"],
       textarea[data-question-id="${questionId}"],
       select[data-question-id="${questionId}"]`
    )

    // Check radio buttons - if any radio is checked, question is answered
    const radios = Array.from(inputs).filter(input => input.type === 'radio')
    if (radios.length > 0) {
      return radios.some(radio => radio.checked)
    }

    // Check text/email/date/tel inputs - if any have a value, question is answered
    const textInputs = Array.from(inputs).filter(input =>
      ['text', 'email', 'date', 'tel', 'number'].includes(input.type) ||
      input.tagName === 'TEXTAREA'
    )
    if (textInputs.length > 0) {
      return textInputs.some(input => input.value && input.value.trim() !== '')
    }

    // Check selects - if any have a selected value, question is answered
    const selects = Array.from(inputs).filter(input => input.tagName === 'SELECT')
    if (selects.length > 0) {
      return selects.some(select => select.value && select.value !== '')
    }

    return false
  }

  // Check if any data containers (for specialized questions like dependents, state residencies, care expenses)
  // have visible rows that would be cleared
  hasDataContainerRows() {
    // If no dataContainer targets are registered, no specialized data exists
    if (!this.hasDataContainerTarget) {
      return false
    }

    // Check each data container for visible child rows
    return this.dataContainerTargets.some(container => {
      // Count visible rows (not hidden or display:none)
      const visibleRows = Array.from(container.children).filter(row => {
        return row.style.display !== 'none' && !row.hidden
      })

      return visibleRows.length > 0
    })
  }

  // Get confirmation message
  getConfirmationMessage() {
    return 'Marking this as "Does Not Apply" will clear all entered data. Continue?'
  }
}
